/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {ClaimsApi} from '../../api/ms-claims/claims.api';
import {Subject} from 'rxjs';
import {Router} from '@angular/router';
import {ClaimCommandModel} from '../../api/ms-claims/contracts/claim-command.model';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ClaimSubmitStatusEnum} from '../../api/ms-claims/contracts/claim-submit-status.enum';
import {ClaimBaseModel} from '../../api/ms-claims/contracts/claim-base.model';
import {HttpResponse} from '@angular/common/http';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {CreateAndSubmitActionsEnum} from '../../api/generic/contracts/create-and-submit-actions.enum';
import {ApiErrorModel} from '../../api/generic/contracts/api-error.model';
import {MultiStatusModel} from '../../api/generic/contracts/multi-status.model';

@Injectable({
    providedIn: 'root'
})
export class ClaimSendHelperService {

    loadingSubject = new Subject<boolean>();
    enableLoading = this.loadingSubject.asObservable();
    private readonly SUBMIT_MODAL_WITH = 615;
    private viewDetailHasError = false;

    constructor(private claimsApi: ClaimsApi,
                private toast: ToastWrapperService,
                private router: Router,
                private loading: LoadingService,
                private modalService: NgbModal) {
    }

    sendClaim(number: string, plantCode: string, materialNumber: string, successCallback: () => void = (): void => {
    }): void {
        this.sendClaimConfirmationModal().confirmEvent.subscribe(() => {
            this.loadingSubject.next(true);
            this.claimsApi.submitSavedClaim(number).subscribe((res) => {
                successCallback();
                this.showSendClaimSuccessToast(number);
            }, error => {
                this.viewDetailHasError = true;
                this.manageSubmitErrorToast(error.error, number, plantCode, materialNumber);
            }).add(() => {
                if (this.router.routerState.snapshot.url !== '/claim' && this.viewDetailHasError) {
                    this.router.navigate(['/', 'claim', number]);
                } else {
                    this.router.navigate(['/', 'claim']);
                }
                this.loadingSubject.next(false);
            });
        });
    }

    createAndSendClaim(claim: ClaimCommandModel): void {
        this.sendClaimConfirmationModal().confirmEvent.subscribe(() => {
            this.loading.start();
            this.claimsApi.createAndSubmitClaim(claim)
                    .subscribe(response => {
                        this.manageCreateAndSendResponse(response, claim.supplyingPackingPlantCode, claim.materialNumber);
                        this.router.navigate(['/', 'claim']);
                    })
                    .add(() => {
                        this.loading.stop();
                    });
        });
    }

    manageCreateAndSendResponse(response: HttpResponse<any>, plantCode: string, materialNumber: string): void {
        if (response.status === 207) {
            const multiStatus: MultiStatusModel = response.body;
            const createAction = multiStatus.actions.find(status => status.action === CreateAndSubmitActionsEnum.CREATE);
            const submitAction = multiStatus.actions.find(status => status.action === CreateAndSubmitActionsEnum.SUBMIT);
            this.manageSubmitErrorToast(submitAction.error, submitAction.identifier, plantCode, materialNumber);
        } else {
            const {number}: ClaimBaseModel = response.body;
            this.showSendClaimSuccessToast(number);
        }
    }

    manageSubmitErrorToast(apiError: ApiErrorModel, number: string, plantCode: string, materialNumber: string): void {
        if (apiError) {
            switch (apiError.code as ClaimSubmitStatusEnum) {
                case ClaimSubmitStatusEnum.INVALID:
                    this.showHuValidationInvalidToast(number, plantCode, materialNumber);
                    break;
                case ClaimSubmitStatusEnum.MISSING:
                    this.showDataMissingToast(number);
                    break;
                case ClaimSubmitStatusEnum.HANDLING_UNIT_SERVICE_UNAVAILABLE:
                    this.showHuValidationUnavailableToast(number);
                    break;
                case ClaimSubmitStatusEnum.FILE_MANAGER_SERVICE_UNAVAILABLE:
                    this.showDataGeneralErrorMissingToast(number, apiError.message);
                    break;
                default:
                    // In this case will show by default the confirmation modal with the unexpected error.
            }
        }
    }

    showDataGeneralErrorMissingToast(claimNumber: string, message: string): void {
        this.toast.error(`Claim No. <span data-cy="toast-new-claim-number">${claimNumber}</span>
                                <strong>was not sent</strong>. ${message}`, 'Error');
    }

    showSendClaimSuccessToast(claimNumber: string): void {
        this.toast.success(`Claim No.<span class="toast-new-claim-number" data-cy="toast-new-claim-number">${claimNumber} </span>`
                                    +`<strong>sent</strong> successfully`, 'Success');
    }

    showHuValidationUnavailableToast(claimNumber: string): void {
        this.toast.error(`Claim No. <span data-cy="toast-new-claim-number">${claimNumber}</span>`
        +`<strong> was not sent</strong>. HU validation is unavailable at the moment.`, 'Error');
    }

    showHuValidationInvalidToast(claimNumber: string, plantCode: string, materialNumber: string): void {
        this.toast.error(`Claim No. <span data-cy="toast-new-claim-number">${claimNumber}</span><strong> was not sent</strong>.`
                                  +`<strong> Please check your entries</strong> for one or more HUs with material ${materialNumber}.`
                , 'Error');
    }

    showDataMissingToast(claimNumber: string): void {
        this.toast.error(`Claim No. <span data-cy="toast-new-claim-number">${claimNumber}</span><strong> was not sent</strong>.`
                                  +`Some information is missing. Please check your entries.`, 'Error');
    }

    sendClaimConfirmationModal(): ConfirmModalComponent {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        return confirmModalInstance.initModal({
            titleText: 'Confirm',
            subtitleText: `Are you sure you want to send the Claim?`,
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            primaryButtonText: 'Send',
            secondaryButtonText: 'Cancel',
            width: this.SUBMIT_MODAL_WITH,
            manualChecks: {
                title: `<strong>Be aware that:</strong>`,
                inputs: [
                    {
                        checked: false,
                        message: 'This action will send the Claim to BMW and the process will be started'
                    },
                    {
                        checked: false,
                        message: 'Edit and Cancel options for submitted claims are not allowed'
                    },
                    {
                        checked: false,
                        message: 'I am aware that freight costs are only reimbursed if the SPO field is filled out correctly.' +
                                ' In case of incorrect completion, I waive reimbursement of the freight costs.'
                    }
                ]
            }
        } as ConfirmModal);
    }
}
